import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { useLocation, useNavigate } from 'react-router-dom';

interface Caixa {
  top: number;
  left: number;
  width: number;
  height: number;
  raio: number;
}

interface EstadoCortina {
  pedido: { caixa: Caixa; destino: string | (() => void) } | null;
  /** Abre a cortina a partir do botão (caixa na tela) e leva até o destino. */
  abrir: (caixa: Caixa, destino: string | (() => void)) => void;
  fechar: () => void;
}

export const useCortinaDeAula = create<EstadoCortina>((set) => ({
  pedido: null,
  abrir: (caixa, destino) => set({ pedido: { caixa, destino } }),
  fechar: () => set({ pedido: null }),
}));

const COBRIR = { duration: 450, easing: 'cubic-bezier(0.7, 0, 0.2, 1)' };
const SUBIR = { duration: 550, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' };
/** Se a página nova demorar, a cortina sobe assim mesmo. */
const ESPERA_MAXIMA = 3000;

/**
 * A passagem do botão de play para a aula (pedido dele, 29/10): o botão verde
 * cresce até cobrir a tela inteira, a página troca por baixo e a cortina sobe
 * mostrando a aula. Sem fusão entre as duas telas: a cor cheia entra e sai
 * pela borda (a regra de "botão que enche a tela" do estúdio). Mora uma vez
 * no App, fora das rotas, porque precisa sobreviver à troca de página.
 */
export function CortinaDeAula() {
  const pedido = useCortinaDeAula((s) => s.pedido);
  const fechar = useCortinaDeAula((s) => s.fechar);
  const cortina = useRef<HTMLDivElement>(null);
  const esperando = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  const subir = () => {
    const el = cortina.current;
    if (!el || !esperando.current) return;
    esperando.current = false;
    // um quadro para a página nova pintar antes de aparecer
    requestAnimationFrame(() => {
      el.animate(
        [
          { clipPath: 'inset(0px 0px 0px 0px)' },
          { clipPath: 'inset(0px 0px 100% 0px)' },
        ],
        { ...SUBIR, fill: 'forwards' },
      ).finished.then(() => {
        el.style.visibility = 'hidden';
        el.getAnimations().forEach((a) => a.cancel());
        fechar();
      });
    });
  };

  useEffect(() => {
    const el = cortina.current;
    if (!pedido || !el) return;
    const { caixa, destino } = pedido;
    const direita = innerWidth - caixa.left - caixa.width;
    const baixo = innerHeight - caixa.top - caixa.height;
    el.style.visibility = 'visible';
    const cobrir = el.animate(
      [
        {
          clipPath: `inset(${caixa.top}px ${direita}px ${baixo}px ${caixa.left}px round ${caixa.raio}px)`,
        },
        { clipPath: 'inset(0px 0px 0px 0px round 0px)' },
      ],
      { ...COBRIR, fill: 'forwards' },
    );
    let limite = 0;
    cobrir.finished
      .then(() => {
        esperando.current = true;
        if (typeof destino === 'function') destino();
        else navigate(destino);
        limite = window.setTimeout(subir, ESPERA_MAXIMA);
      })
      .catch(() => {});
    return () => window.clearTimeout(limite);
  }, [pedido]);

  // a página nova chegou: a cortina sobe
  useEffect(() => {
    subir();
  }, [location.key]);

  return (
    <div
      ref={cortina}
      aria-hidden="true"
      className="bg-primary fixed inset-0 z-[9999]"
      style={{ visibility: 'hidden' }}
    />
  );
}
